/**
 * Respostas rápidas — Provider + hook compartilhados (fonte única de verdade).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { QuickReply } from '../../../../types';
import { quickRepliesRepository } from '../../../../infrastructure/repositories/SupabaseQuickRepliesRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';

interface QuickRepliesContextData {
    replies: QuickReply[];
    loading: boolean;
    createReply: (data: Omit<QuickReply, 'id'>) => Promise<void>;
    updateReply: (id: string, data: Partial<QuickReply>) => Promise<void>;
    deleteReply: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    selectedIds: Set<string>;
    toggleSelection: (id: string) => void;
    toggleAllSelection: (filteredReplies: QuickReply[]) => void;
    clearSelection: () => void;
    refetch: () => Promise<void>;
}

const QuickRepliesContext = createContext<QuickRepliesContextData | undefined>(undefined);

export const QuickRepliesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [replies, setReplies] = useState<QuickReply[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const loadReplies = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const data = await quickRepliesRepository.findAll(currentOrganization.id);
            setReplies(data);
        } catch (error) {
            console.error('Error loading quick replies:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (currentOrganization?.id) {
            loadReplies();
        }
    }, [currentOrganization?.id, loadReplies]);

    const createReply = useCallback(async (data: Omit<QuickReply, 'id'>) => {
        if (!currentOrganization?.id) return;

        const newReply = await quickRepliesRepository.create(data, currentOrganization.id);
        setReplies(prev => [newReply, ...prev]);
    }, [currentOrganization?.id]);

    const updateReply = useCallback(async (id: string, data: Partial<QuickReply>) => {
        if (!currentOrganization?.id) return;

        const updatedReply = await quickRepliesRepository.update(id, data, currentOrganization.id);
        setReplies(prev => prev.map(reply => (reply.id === id ? updatedReply : reply)));
    }, [currentOrganization?.id]);

    const deleteReply = useCallback(async (id: string) => {
        if (!currentOrganization?.id) return;

        setReplies(prev => prev.filter(reply => reply.id !== id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        try {
            await quickRepliesRepository.delete(id, currentOrganization.id);
        } catch (error) {
            console.error('Error deleting quick reply:', error);
            await loadReplies();
            throw error;
        }
    }, [currentOrganization?.id, loadReplies]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback((filteredReplies: QuickReply[]) => {
        setSelectedIds(prev => {
            if (prev.size === filteredReplies.length && filteredReplies.length > 0) {
                return new Set();
            }
            return new Set(filteredReplies.map(r => r.id));
        });
    }, []);

    const deleteBulk = useCallback(async (ids: string[]) => {
        if (!currentOrganization?.id || ids.length === 0) return;

        setReplies(prev => prev.filter(reply => !ids.includes(reply.id)));
        setSelectedIds(new Set());

        try {
            await Promise.all(ids.map(id => quickRepliesRepository.delete(id, currentOrganization.id!)));
        } catch (error) {
            console.error('Error deleting quick replies:', error);
            await loadReplies();
            throw error;
        }
    }, [currentOrganization?.id, loadReplies]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const value: QuickRepliesContextData = {
        replies,
        loading,
        createReply,
        updateReply,
        deleteReply,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch: loadReplies,
    };

    return React.createElement(QuickRepliesContext.Provider, { value }, children);
};

export const useQuickReplies = (): QuickRepliesContextData => {
    const context = useContext(QuickRepliesContext);
    if (!context) {
        throw new Error('useQuickReplies must be used within QuickRepliesProvider');
    }
    return context;
};
